/**
 * @module idleTimerService
 * @description Service for handling idle timer functionality.
 */

/**
 * @interface ITimer
 * @description Represents the state of the idle timer.
 * @property {number} timeout - The timeout duration in milliseconds.
 * @property {boolean} showModal - Indicates whether to show the modal.
 * @property {boolean} userLoggedIn - Indicates whether the user is logged in.
 * @property {boolean} isTimedOut - Indicates whether the timer has timed out.
 * @property {any} counter - The counter object.
 */
export interface ITimer {
	timeout: number
	showModal: boolean
	userLoggedIn: boolean
	isTimedOut: boolean
	counter: any
}

/**
 * @namespace idleTimerService
 * @description Service methods for handling idle timer functionality.
 */
export const idleTimerService = {
	/**
	 * @function onAction
	 * @description Callback function to be executed when user performs an action.
	 * @param {function} setTimer - Function to update the timer state.
	 */
	onAction: (setTimer: any) => {
		setTimer((prev: any) => ({
			...prev,
			isTimedOut: false,
		}))
	},

	/**
	 * @function onActive
	 * @description Callback function to be executed when user is active.
	 * @param {function} setTimer - Function to update the timer state.
	 */
	onActive: (setTimer: any) => {
		console.log('user is active')
		setTimer((prev: any) => ({
			...prev,
			isTimedOut: false,
		}))
	},

	/**
	 * @function onIdle
	 * @description Callback function to be executed when user is idle.
	 * @param {function} setTimer - Function to update the timer state.
	 * @param {ITimer} timer - The current timer state.
	 */
	onIdle: (setTimer: any, timer: any) => {
		if (!timer.isTimedOut) {
			setTimer((prev: any) => ({
				...prev,
				showModal: true,
				isTimedOut: true,
			}))
		} else {
			// Never reach this code with current settings
			setTimer((prev: any) => ({
				...prev,
				showModal: false,
			}))
		}
	},

	/**
	 * @function handleClose
	 * @description Handles the closing of the idle timer modal.
	 * @param {any} idleTimer - The idle timer object.
	 * @param {function} setTimer - Function to update the timer state.
	 */
	handleClose: (idleTimer: any, setTimer: any) => {
		setTimer((prev: any) => ({
			...prev,
			showModal: false,
			isTimedOut: false,
		}))
		console.log('..user closed modal')
		idleTimer.current.reset() // Reset idle timer to initial settings
	},

	/**
	 * @function handleLogout
	 * @description Handles the user logout process.
	 * @param {function} navigate - Function to navigate to a different page.
	 * @param {function} setTimer - Function to update the timer state.
	 */
	handleLogout: (navigate: any, setTimer: any) => {
		setTimer((prev: any) => ({
			...prev,
			showModal: false,
		}))
		navigate('/logout')
		console.log('..user is logged out')
	},
}
